import React, { useState, useEffect } from "react";
import "../pages/AdminProjects.css"; 
import bfLogo from "../assets/bf_logo.png";
import { useNavigate } from "react-router-dom";
import { Trash2, FileText, CheckCircle, Upload, Users, Download, X } from "lucide-react"; 
import { useTranslation } from "react-i18next"; 

/* ===== PDF BIBLIOTEKOS ===== */
import jsPDF from "jspdf"; 
import autoTable from "jspdf-autotable";

/* ===== FIREBASE ===== */
import { db, storage, auth } from "../firebase";
import { collection, addDoc, deleteDoc, doc, onSnapshot, query, where, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";

const latinize = (str) => {
    if (!str) return "";
    const map = {
        'ą': 'a', 'č': 'c', 'ę': 'e', 'ė': 'e', 'į': 'i', 'š': 's', 'ų': 'u', 'ū': 'u', 'ž': 'z',
        'Ą': 'A', 'Č': 'C', 'Ę': 'E', 'Ė': 'E', 'Į': 'I', 'Š': 'S', 'Ų': 'U', 'Ū': 'U', 'Ž': 'Z',
        'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss', 'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue'
    };
    return str.replace(/[ąčęėįšųūžĄČĘĖĮŠŲŪŽäöüßÄÖÜ]/g, match => map[match]);
};

export default function AdminCompanyDocuments() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); // Inicializuojame t funkciją
  
  const [docs, setDocs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState(""); 
  const [requireSign, setRequireSign] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const uSnap = await getDoc(doc(db, "users", user.uid));
        if (uSnap.exists()) {
          const uData = uSnap.data();
          const masterId = uData.belongsTo || user.uid;
          const masterSnap = await getDoc(doc(db, "users", masterId));
          if (masterSnap.exists()) setUserData(masterSnap.data());
        }
      } else navigate("/admin/login");
    });
    return () => unsubscribe();
  }, [navigate]);

  const masterId = userData?.belongsTo || auth.currentUser?.uid;

  useEffect(() => {
    if (!masterId) return;
    const loadData = () => {
        const unsubDocs = onSnapshot(query(collection(db, "company_docs"), where("adminId", "==", masterId)), (snap) => {
            const fetchedDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const sortedDocs = fetchedDocs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            setDocs(sortedDocs);
            setLoading(false);
        });
        const unsubWorkers = onSnapshot(query(collection(db, "workers"), where("adminId", "==", masterId)), (snap) => {
            setWorkers(snap.docs.map(d => ({ id: d.id, name: d.data().name })));
        });
        return () => { unsubDocs(); unsubWorkers(); };
    };
    return loadData(); 
  }, [masterId]);

  const getAssignedNames = (assignedTo) => {
      if (assignedTo === 'all') return t('all_workers');
      if (!Array.isArray(assignedTo)) return "";
      return assignedTo.map(id => workers.find(w => w.id === id)?.name || "???").join(", ");
  };

  const generatePDF = (docItem) => {
    if (!userData) return;
    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.setFontSize(22);
    pdf.text(latinize(userData?.companyName || "mindo"), 14, 20);
    pdf.setFontSize(9);
    pdf.setTextColor(100);
    const addr = userData?.address;
    const displayAddr = typeof addr === 'object' ? `${addr.street || ''} ${addr.houseNumber || ''}, ${userData.zip || ''} ${userData.city || ''}` : (addr || "");
    pdf.text(latinize(`${displayAddr} | ${userData?.email || ''}`), 14, 26);
    pdf.setDrawColor(0, 255, 136); pdf.setLineWidth(0.8); pdf.line(14, 30, 196, 30);

    pdf.setFontSize(16); pdf.setTextColor(0); pdf.setFont(undefined, 'bold');
    pdf.text(latinize(t('protocol_title')), 14, 45); // Naudojame vertimą PDF antraštei

    autoTable(pdf, {
      startY: 50,
      body: [
        [t('document_label') + ":", latinize(docItem.title)],
        [t('date') + ":", new Date(docItem.createdAt).toLocaleDateString(i18n.language === 'lt' ? 'lt-LT' : 'de-DE')],
        [t('status') + ":", docItem.signatures_data?.length > 0 ? t('signed') : t('waiting')]
      ],
      theme: 'plain',
      styles: { fontSize: 10 }
    });

    pdf.setFontSize(10); pdf.setTextColor(0);
    pdf.text(t('file_link') + ":", 14, pdf.lastAutoTable.finalY + 10);
    pdf.setTextColor(0, 0, 255);
    pdf.textWithLink(latinize(t('click_to_open')), 14, pdf.lastAutoTable.finalY + 18, { url: docItem.fileUrl });
    
    pdf.setDrawColor(200); pdf.line(14, pdf.lastAutoTable.finalY + 22, 196, pdf.lastAutoTable.finalY + 22);
    pdf.setTextColor(0); pdf.text(t('signatures_section'), 14, pdf.lastAutoTable.finalY + 32);

    let currentY = pdf.lastAutoTable.finalY + 40;
    (docItem.signatures_data || []).forEach(sig => {
        if (currentY > 240) { pdf.addPage(); currentY = 20; }
        pdf.setFontSize(10); pdf.text(latinize(`${t('worker')}: ${sig.name}`), 14, currentY);
        pdf.setFontSize(8); pdf.text(`${t('date')}: ${new Date(sig.signedAt).toLocaleString(i18n.language === 'lt' ? 'lt-LT' : 'de-DE')}`, 14, currentY + 6);
        if (sig.signature) try { pdf.addImage(sig.signature, "PNG", 14, currentY + 8, 45, 18); } catch(e){}
        currentY += 45;
    });
    pdf.save(`Protokoll_${latinize(docItem.title)}.pdf`);
  };

  const handleCreate = async () => {
    if (!newTitle || !masterId) return alert(t('error_title_required'));
    setUploading(true);
    let fileUrl = "";
    try {
        if (file) {
            const storageRef = ref(storage, `documents/${masterId}/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file, { contentType: file.type });
            fileUrl = await getDownloadURL(storageRef);
        }
        await addDoc(collection(db, "company_docs"), {
            adminId: masterId, title: newTitle, content: newContent, fileUrl,
            fileName: file ? file.name : "", requireSignature: requireSign, createdAt: Date.now(),
            signatures: [], signatures_data: [], 
            assignedTo: sendToAll ? 'all' : selectedWorkerIds
        });
        setNewTitle(""); setNewContent(""); setFile(null); setSelectedWorkerIds([]); setSendToAll(true); setShowForm(false);
    } catch (e) { console.error(e); }
    setUploading(false);
  };

  if (loading && !docs.length) return <div style={{padding: '20px', color: 'white'}}>{t('loading')}</div>;

  return (
    <div className="projects-container">
      <button className="back-btn" onClick={() => navigate(-1)} style={{ position: "absolute", top: "30px", left: "30px", zIndex: 100 }}>← {t('back')}</button>
      <div className="projects-content">
        <img src={bfLogo} alt="BF" className="projects-logo" />
        <h1 className="projects-title">{t('company_documents')}</h1>

        <div className="projects-topbar">
          <button className="open-btn" onClick={() => setShowForm(!showForm)}>
            {showForm ? t('cancel') : `+ ${t('addDocument')}`} 
          </button>
        </div>

        {showForm && (
            <div style={{background: '#1e293b', padding: '25px', borderRadius: '15px', border: '1px solid #334155', marginBottom: '20px'}}>
                <input placeholder={t('documentName')} value={newTitle} onChange={e=>setNewTitle(e.target.value)} style={{width: '100%', padding: '12px', marginBottom: '10px', background: '#0f172a', border: '1px solid #475569', color: 'white', borderRadius: '8px'}}/>
                <textarea placeholder={t('description')} value={newContent} onChange={e=>setNewContent(e.target.value)} style={{width: '100%', height: '60px', padding: '12px', marginBottom: '15px', background: '#0f172a', border: '1px solid #475569', color: 'white', borderRadius: '8px'}}/>
                
                <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                    <label style={{background: '#334155', padding: '10px 20px', borderRadius: '8px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <Upload size={18}/> {file ? file.name : t('uploadFile')}
                        <input type="file" hidden onChange={e => setFile(e.target.files[0])} />
                    </label>
                    <button onClick={handleCreate} disabled={uploading} style={{background: '#00ff88', color: 'black', padding: '10px 30px', border: 'none', borderRadius: '8px', fontWeight: 'bold'}}>
                        {uploading ? t('sending') : t('send')}
                    </button>
                </div>

                <div style={{background: '#0f172a', padding: '15px', borderRadius: '10px', border: '1px solid #334155', marginBottom: '15px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', color: '#38bdf8'}}>
                        <input type="checkbox" checked={sendToAll} onChange={e=>setSendToAll(e.target.checked)} style={{width: '18px', height: '18px'}}/>
                        <label>{t('send_to_all')}</label> {/* Pataisyta! */}
                    </div>
                    {!sendToAll && (
                        <div style={{maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '25px'}}>
                            {workers.map(w => (
                                <div key={w.id} style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#ccc'}}>
                                    <input type="checkbox" checked={selectedWorkerIds.includes(w.id)} onChange={() => setSelectedWorkerIds(prev => prev.includes(w.id) ? prev.filter(x => x !== w.id) : [...prev, w.id])}/>
                                    <span>{w.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div style={{display: 'flex', alignItems: 'center', gap: '10px', color: 'white'}}>
                    <input type="checkbox" checked={requireSign} onChange={e=>setRequireSign(e.target.checked)} style={{width: '18px', height: '18px'}}/>
                    <label>{t('require_signature')}</label> {/* Pataisyta! */}
                </div>
            </div>
        )}

        <div style={{ background: '#0f172a', borderRadius: '15px', border: '1px solid #334155', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', color: 'white', textAlign: 'left' }}>
                <thead style={{ background: '#1e293b', color: '#94a3b8', textTransform: 'uppercase', fontSize: '12px' }}>
                    <tr>
                        <th style={{ padding: '15px' }}>{t('documents')}</th>
                        <th style={{ padding: '15px' }}>{t('created_at')}</th>
                        <th style={{ padding: '15px' }}>{t('status')}</th>
                        <th style={{ padding: '15px', textAlign: 'right' }}>{t('actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {docs.map(docItem => (
                        <tr key={docItem.id} style={{ borderBottom: '1px solid #1e293b' }}>
                            <td style={{ padding: '15px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <FileText size={18} color="#38bdf8" />
                                        <span 
                                          onClick={() => navigate(`/admin/documents/${docItem.id}`)}
                                          style={{color: '#38bdf8', cursor: 'pointer', fontWeight: 'bold'}}
                                        >
                                          {docItem.title}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#ffd400', paddingLeft: '28px' }}>
                                        <Users size={12} style={{ marginRight: '5px', verticalAlign: 'middle' }}/>
                                        {getAssignedNames(docItem.assignedTo)}
                                    </div>
                                </div>
                            </td>
                            <td style={{ padding: '15px', color: '#94a3b8' }}>{new Date(docItem.createdAt).toLocaleDateString(i18n.language === 'lt' ? 'lt-LT' : 'de-DE')}</td>
                            <td style={{ padding: '15px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: docItem.signatures_data?.length > 0 ? '#00ff88' : '#ffd400' }}>
                                    <CheckCircle size={14} />
                                    <span>{docItem.signatures_data?.length || 0} {t('signed')}</span>
                                </div>
                            </td>
                            <td style={{ padding: '15px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button className="open-btn" style={{ border: '1px solid #00ff88', color: '#00ff88' }} onClick={() => generatePDF(docItem)}><Download size={18}/></button>
                                    <button className="open-btn" style={{ border: '1px solid #ff4444', color: '#ff4444' }} onClick={async () => { if(window.confirm(t('sureDelete'))) await deleteDoc(doc(db, "company_docs", docItem.id)) }}><Trash2 size={18}/></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
}